import React from "react";
import { H3, Text, Button, Card, ControlGroup, Elevation, InputGroup, Popover, Tag } from "@blueprintjs/core";
import { DatePicker } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import { apiCallPatch, niceDateTime, apiCallPost } from "../../../../micro/fns/util";
import SingleSelectWrapper from "../../../../micro/utils/SingleSelectWrapper";
import AsyncSelWrap from "../../../../micro/utils/AsyncSelWrap";
import cogoToast from "cogo-toast";
import _ from "lodash";
import moment from "moment";

class KeyEventsTab extends React.Component {
  state = {
    // Key Events state
    selectedDate: new Date(),
    editingEventIndex: -1, // Index of event being edited, -1 means none

    // Key People state
    userSearching: false,
    userSearchResults: [],
    userSelected: null,
    role: null,

    // Data
    keyEvents: [],
    keyPeople: [],

    // Originals for audit diffs
    originalKeyEvents: [],
    originalKeyPeople: [],

    // Loading states
    isLoading: false,
    isSaving: false,
    isSavingKeyEvents: false,
    isSavingKeyPeople: false,

    // Dirty states
    isKeyEventsDirty: false,
    isKeyPeopleDirty: false,
  };

  componentDidMount() {
    // Initialize with props data if available
    console.log("KeyEventsTab componentDidMount - Props:", {
      keyEvents: this.props.keyEvents,
      keyPeople: this.props.keyPeople,
      data: this.props.data,
    });

    this.setState({
      keyEvents: this.props.keyEvents || [],
      keyPeople: this.props.keyPeople || [],
      originalKeyEvents: JSON.parse(JSON.stringify(this.props.keyEvents || [])),
      originalKeyPeople: JSON.parse(JSON.stringify(this.props.keyPeople || [])),
    });
  }

  componentDidUpdate(prevProps) {
    // Update state when props change
    if (prevProps.keyEvents !== this.props.keyEvents || prevProps.keyPeople !== this.props.keyPeople) {
      console.log("KeyEventsTab componentDidUpdate - Props changed:", {
        keyEvents: this.props.keyEvents,
        keyPeople: this.props.keyPeople,
      });

      this.setState({
        keyEvents: this.props.keyEvents || [],
        keyPeople: this.props.keyPeople || [],
      });

      // Sync originals when not dirty
      if (!this.state.isKeyEventsDirty && !this.state.isKeyPeopleDirty) {
        this.setState({
          originalKeyEvents: JSON.parse(JSON.stringify(this.props.keyEvents || [])),
          originalKeyPeople: JSON.parse(JSON.stringify(this.props.keyPeople || [])),
        });
      }
    }
  }

  // Key Events configuration
  events = [
    "Arrival",
    "Admission",
    "Block Room",
    "Anaesthesia Start",
    "Surgery Start",
    "Surgery End",
    "Anaesthesia End",
    "Moved To Ward",
    "Discharge",
    "Patient Medication",
  ];

  // Key People roles configuration
  roles = [
    "Admission",
    "Block Room Prep Assistant",
    "Anaesthesia Assistant",
    "OT Prep Assistant",
    "Consumable/Implant Prep Assistant",
    "Sterilization",
    "Floor Assistant",
    "Follow-Up & Explanation",
    "Discharge",
    "Patient Medication",
  ];

  // User search functionality
  search = async (string, searchResArray, isSearchingShowSpinner, selectedItem, apiUrl) => {
    if (!string) {
      this.setState({
        [searchResArray]: [],
        [selectedItem]: null,
      });
      return;
    }
    try {
      this.setState({ [isSearchingShowSpinner]: true });
      let res = await apiCallPost(`/users/${this.props.authStore.prefix}/search`, {
        prefix: string || "",
      });
      const results = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const q = String(string || "").trim().toLowerCase();
      const meId = this.props.authStore?.id;
      const meName = this.props.authStore?.name || "";
      const meEmail = this.props.authStore?.email || "";
      const alreadyHasMe = results.some(
        (u) => String(u?._id) === String(meId)
      );
      const merged =
        meId &&
        !alreadyHasMe &&
        q &&
        (`${meName} ${meEmail}`.toLowerCase().includes(q))
          ? [
              {
                _id: meId,
                name: meName || meEmail || "Current user",
                email: meEmail,
              },
              ...results,
            ]
          : results;
      this.setState({
        [searchResArray]: merged,
        [isSearchingShowSpinner]: false,
      });
    } catch (err) {
      cogoToast.error("There was an error in searching. Please check the console for more details.");
      console.log(err);
    }
  };

  guardLocked = () => {
    if (this.props.canEdit === false) {
      cogoToast.warn("This field is locked after discharge/cancellation");
      return true;
    }
    return false;
  };

  // Update data handler
  updateData = (updates) => {
    // Track which data is being updated
    if (updates.keyEvents !== undefined) {
      this.setState({ isKeyEventsDirty: true });
    }
    if (updates.keyPeople !== undefined) {
      this.setState({ isKeyPeopleDirty: true });
    }
    this.setState(updates, () => {
      // Propagate granular updates up to parent so state survives tab unmount/remount
      if (updates.keyEvents !== undefined && typeof this.props.onKeyEventsUpdate === "function") {
        this.props.onKeyEventsUpdate(this.state.keyEvents);
      }
      if (updates.keyPeople !== undefined && typeof this.props.onKeyPeopleUpdate === "function") {
        this.props.onKeyPeopleUpdate(this.state.keyPeople);
      }
      // Backward compatibility: optional aggregate handler
      if (typeof this.props.onUpdate === "function") {
        this.props.onUpdate(updates);
      }
    });
  };

  // Create detailed audit logs for key events, key people, and case number
  createDetailedAuditLogs = async (type) => {
    try {
      const entries = [];
      const resourceId = this.props.data?.id;
      const resourceName = `Surgery - ${this.props.data?.patientName || "Unknown Patient"}`;

      const safe = (v) => (v === null || v === undefined ? "" : v);
      const display = (v, fallback = "Empty") => (v === null || v === undefined || v === "" ? fallback : v);

      if (type === "keyEvents") {
        const original = this.state.originalKeyEvents || [];
        const current = this.state.keyEvents || [];

        const findByEvent = (list, ev) => list.find((x) => x.event === ev);

        // Added events
        current.forEach((cur) => {
          const orig = findByEvent(original, cur.event);
          if (!orig) {
            entries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId,
              resourceName,
              actionType: "ADD_KEY_EVENT",
              actionSlug: `Added key event: "${cur.event}" at ${display(niceDateTime(cur.ts))}`,
              actionMetadata: {
                fieldName: "keyEvents",
                changeType: "ADD",
                previousValue: "",
                newValue: cur.event,
                itemDetails: {
                  event: cur.event,
                  ts: safe(cur.ts),
                  notes: safe(cur.notes || ""),
                },
              },
            });
          }
        });

        // Removed events
        original.forEach((orig) => {
          const cur = findByEvent(current, orig.event);
          if (!cur) {
            entries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId,
              resourceName,
              actionType: "REMOVE_KEY_EVENT",
              actionSlug: `Removed key event: "${orig.event}"`,
              actionMetadata: {
                fieldName: "keyEvents",
                changeType: "REMOVE",
                previousValue: orig.event,
                newValue: "",
                itemDetails: {
                  event: orig.event,
                  ts: safe(orig.ts),
                  notes: safe(orig.notes || ""),
                },
              },
            });
          }
        });

        // Updated fields on existing events
        current.forEach((cur) => {
          const orig = findByEvent(original, cur.event);
          if (orig) {
            // ts change
            const origTs = orig.ts ? new Date(orig.ts).toISOString() : "";
            const curTs = cur.ts ? new Date(cur.ts).toISOString() : "";
            if (origTs !== curTs) {
              entries.push({
                resourceType: "SURGERY_BOOKING",
                resourceId,
                resourceName,
                actionType: "UPDATE",
                actionSlug: `Updated ${cur.event} time: "${display(niceDateTime(orig.ts))}" → "${display(
                  niceDateTime(cur.ts)
                )}"`,
                actionMetadata: {
                  fieldName: "keyEvents.ts",
                  changeType: "UPDATE",
                  previousValue: safe(origTs),
                  newValue: safe(curTs),
                  itemDetails: {
                    event: cur.event,
                  },
                },
              });
            }

            // notes change
            const origNotes = orig.notes || "";
            const curNotes = cur.notes || "";
            if (origNotes !== curNotes) {
              entries.push({
                resourceType: "SURGERY_BOOKING",
                resourceId,
                resourceName,
                actionType: "UPDATE",
                actionSlug: `Updated ${cur.event} notes: "${display(origNotes)}" → "${display(curNotes)}"`,
                actionMetadata: {
                  fieldName: "keyEvents.notes",
                  changeType: "UPDATE",
                  previousValue: safe(origNotes),
                  newValue: safe(curNotes),
                  itemDetails: {
                    event: cur.event,
                  },
                },
              });
            }
          }
        });
      }

      if (type === "keyPeople") {
        const original = this.state.originalKeyPeople || [];
        const current = this.state.keyPeople || [];

        const findByRole = (list, role) => list.find((x) => x.role === role);

        // Added people
        current.forEach((cur) => {
          const orig = findByRole(original, cur.role);
          if (!orig) {
            entries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId,
              resourceName,
              actionType: "ADD_KEY_PERSON",
              actionSlug: `Added key person: "${cur.role}" - ${display(cur.userName)}`,
              actionMetadata: {
                fieldName: "keyPeople",
                changeType: "ADD",
                previousValue: "",
                newValue: cur.role,
                itemDetails: {
                  role: cur.role,
                  userId: safe(cur.userId),
                  userName: safe(cur.userName),
                },
              },
            });
          }
        });

        // Removed people
        original.forEach((orig) => {
          const cur = findByRole(current, orig.role);
          if (!cur) {
            entries.push({
              resourceType: "SURGERY_BOOKING",
              resourceId,
              resourceName,
              actionType: "REMOVE_KEY_PERSON",
              actionSlug: `Removed key person: "${orig.role}" - ${display(orig.userName)}`,
              actionMetadata: {
                fieldName: "keyPeople",
                changeType: "REMOVE",
                previousValue: orig.role,
                newValue: "",
                itemDetails: {
                  role: orig.role,
                  userId: safe(orig.userId),
                  userName: safe(orig.userName),
                },
              },
            });
          }
        });

        // Updated person for same role
        current.forEach((cur) => {
          const orig = findByRole(original, cur.role);
          if (orig) {
            if ((orig.userId || "") !== (cur.userId || "")) {
              entries.push({
                resourceType: "SURGERY_BOOKING",
                resourceId,
                resourceName,
                actionType: "UPDATE",
                actionSlug: `Updated role ${cur.role} person: "${display(orig.userName)}" → "${display(cur.userName)}"`,
                actionMetadata: {
                  fieldName: "keyPeople.userId",
                  changeType: "UPDATE",
                  previousValue: safe(orig.userId || ""),
                  newValue: safe(cur.userId || ""),
                  itemDetails: {
                    role: cur.role,
                    previousUserName: safe(orig.userName),
                    newUserName: safe(cur.userName),
                  },
                },
              });
            }
          }
        });
      }

      if (entries.length > 0) {
        await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxAuditLog/createBulk`, entries);
        console.log(`Created ${entries.length} detailed audit log entries for ${type}.`);
      }
    } catch (err) {
      console.error("Audit log failed:", err);
      // Do not block UX on audit errors
    }
  };

  // Save handler
  onSave = async (type) => {
    try {
      if (this.props.canEdit === false) {
        cogoToast.warn("Editing is locked after discharge");
        return;
      }
      if (!this.props.data || !this.props.data.id) {
        cogoToast.error("Surgery data not found");
        return;
      }
      if (type === "keyEvents") {
        this.setState({ isSavingKeyEvents: true });
      } else {
        this.setState({ isSavingKeyPeople: true });
      }

      // Create detailed audit logs before saving
      await this.createDetailedAuditLogs(type);

      // Format data according to backend schema
      const formattedKeyEvents = this.state.keyEvents.map((event) => ({
        ts: moment(event.ts).toISOString(),
        notes: event.notes || "", // Ensure notes is never undefined
        event: event.event,
      }));

      const formattedKeyPeople = this.state.keyPeople.map((person) => ({
        userName: person.userName,
        userId: person.userId,
        role: person.role,
      }));

      // Preserve the current surgery status so saving key events/people never
      // flips lifecycle to Discharged. Status should change only via the
      // explicit “Mark Discharged” action.
      const preservedStatus = (this.props.data?.status || "").toString().trim();

      await apiCallPatch(`/v3/${this.props.authStore.prefix}/neoSxBookingMaster/update/${this.props.data.id}`, {
        keyEvents: formattedKeyEvents,
        keyPeople: formattedKeyPeople,
        ...(preservedStatus ? { status: preservedStatus } : {}),
      });

      if (type === "keyEvents") {
        this.setState({
          isKeyEventsDirty: false,
          originalKeyEvents: JSON.parse(JSON.stringify(this.state.keyEvents || [])),
        });
        cogoToast.success("Key events saved successfully");
      } else {
        this.setState({
          isKeyPeopleDirty: false,
          originalKeyPeople: JSON.parse(JSON.stringify(this.state.keyPeople || [])),
        });
        cogoToast.success("Key people saved successfully");
      }
    } catch (err) {
      cogoToast.error(`Failed to save ${type === "keyEvents" ? "key events" : "key people"}`);
      console.error(err);
    } finally {
      if (type === "keyEvents") {
        this.setState({ isSavingKeyEvents: false });
      } else {
        this.setState({ isSavingKeyPeople: false });
      }
    }
  };

  // Helper methods for event editing
  startEditingEvent = (eventIndex) => {
    const event = this.state.keyEvents[eventIndex];
    const eventDate = event.ts ? new Date(event.ts) : new Date();
    console.log("Starting edit for event:", event, "Date:", eventDate);
    this.setState({
      editingEventIndex: eventIndex,
      selectedDate: eventDate,
    });
  };

  cancelEditingEvent = () => {
    this.setState({
      editingEventIndex: -1,
      selectedDate: new Date(),
    });
  };

  saveEventEdit = () => {
    const { editingEventIndex, selectedDate, keyEvents } = this.state;
    if (editingEventIndex >= 0) {
      const updatedEvents = [...keyEvents];
      updatedEvents[editingEventIndex] = {
        ...updatedEvents[editingEventIndex],
        ts: selectedDate,
      };
      this.updateData({ keyEvents: updatedEvents });
      this.setState({
        editingEventIndex: -1,
        selectedDate: new Date(),
      });
    }
  };

  // Key Events Methods
  renderEventsTimeline = () => {
    const isLocked = this.props.canEdit === false;
    return this.events.map((each) => {
      let ll = this.state.keyEvents;
      let idx = _.findIndex(ll, (l) => l.event === each);
      const isEditingThis = this.state.editingEventIndex === idx;

      if (idx > -1) {
        return (
          <ControlGroup key={each} style={{ marginTop: "5px" }}>
            <Tag
              large
              intent="success"
              minimal
              icon="endorsed"
              onRemove={isLocked ? undefined : () => {
                let filteredEvents = ll.filter((item, index) => index !== idx);
                this.updateData({ keyEvents: filteredEvents });
              }}
            >
              {each}
            </Tag>

            {isEditingThis ? (
              <Popover
                isOpen={true}
                onClose={this.cancelEditingEvent}
                content={
                  <div style={{ padding: "10px", minWidth: "250px" }}>
                    <div style={{ marginBottom: "10px", fontWeight: "bold" }}>Edit {each} Time</div>
                    <DatePicker
                      timePrecision="minute"
                      value={this.state.selectedDate}
                      onChange={(date) => {
                        console.log("DatePicker onChange:", date);
                        if (date) {
                          this.setState({ selectedDate: date });
                        }
                      }}
                      timePickerProps={{
                        useAmPm: true,
                      }}
                    />
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                      <Button small intent="primary" onClick={this.saveEventEdit}>
                        Save
                      </Button>
                      <Button small onClick={this.cancelEditingEvent}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                }
              >
                <Tag large intent="primary" icon="time" style={{ cursor: "pointer" }}>
                  {niceDateTime(ll[idx].ts)} (editing...)
                </Tag>
              </Popover>
            ) : (
              <Tag
                large
                intent="primary"
                icon="time"
                style={{ cursor: isLocked ? "default" : "pointer" }}
                onClick={isLocked ? undefined : () => this.startEditingEvent(idx)}
                title={isLocked ? undefined : "Click to edit date/time"}
              >
                {niceDateTime(ll[idx].ts)}
              </Tag>
            )}

            <Tag large minimal>
              Notes
            </Tag>
            <InputGroup
              value={ll[idx].notes}
              disabled={isLocked}
              onChange={(event) => {
                ll[idx].notes = event.target.value;
                this.updateData({ keyEvents: ll });
              }}
            />
          </ControlGroup>
        );
      } else {
        return (
          <ControlGroup key={each} style={{ marginTop: "5px" }}>
            <Tag large intent="warning" minimal>
              {each}
            </Tag>
            {isLocked ? (
              <Button outlined disabled text="Select Date and Time" />
            ) : (
              <Popover
                captureDismiss={true}
                onInteraction={(nextOpenState) => {
                  if (nextOpenState) {
                    this.setState({ selectedDate: new Date() });
                  }
                }}
                onClose={() => {
                  if (this.state.selectedDate) {
                    ll.push({
                      event: each,
                      ts: this.state.selectedDate,
                      notes: "",
                    });
                    this.updateData({ keyEvents: ll });
                  }
                }}
                content={
                  <div style={{ padding: "10px" }}>
                    <DatePicker
                      timePrecision="minute"
                      value={this.state.selectedDate}
                      onChange={(date) => {
                        console.log("DatePicker onChange (new event):", date);
                        if (date) {
                          this.setState({ selectedDate: date });
                        }
                      }}
                      timePickerProps={{
                        useAmPm: true,
                      }}
                    />
                  </div>
                }
              >
                <Button outlined text="Select Date and Time" />
              </Popover>
            )}
          </ControlGroup>
        );
      }
    });
  };

  renderKeyEventsSection = () => {
    const isLocked = this.props.canEdit === false;
    return (
      <Card elevation={Elevation.FOUR} style={{ backgroundColor: "var(--bg-surface)", marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Key Events</H3>
          {this.state.isKeyEventsDirty && !isLocked && (
            <Button
              intent="primary"
              icon="floppy-disk"
              onClick={() => this.onSave("keyEvents")}
              loading={this.state.isSavingKeyEvents}
              disabled={this.state.isSavingKeyEvents}
              small
            >
              {this.state.isSavingKeyEvents ? "Saving..." : "Save"}
            </Button>
          )}
        </div>
        <div>
          {this.renderEventsTimeline()}
        </div>
      </Card>
    );
  };

  // Key People Methods
  renderAsyncSearchers = (
    tagText,
    placeHolderText,
    stateSearchingKey,
    stateSearchResultsKey,
    stateSelectedItemKey,
    searchApiUrl,
    handleAddUser
  ) => {
    return (
      <ControlGroup>
        <Tag large minimal style={{ color: "var(--text-primary)" }}>
          {tagText}
        </Tag>
        <AsyncSelWrap
          disabled={this.props.isBusy || this.state.isWaitingOnLineItem}
          placeholder={placeHolderText}
          searching={this.state[stateSearchingKey]}
          items={this.state[stateSearchResultsKey].map((each) => {
            return {
              ...each,
              item: each.name,
              entityTypeString: each.email,
              helper: each._id,
            };
          })}
          selectedItem={null}
          update={(string) =>
            this.search(string, stateSearchResultsKey, stateSearchingKey, stateSelectedItemKey, searchApiUrl)
          }
          onChange={(chosenItem) => {
            if (this.guardLocked()) return;
            this.setState({ [stateSelectedItemKey]: chosenItem }, () => {
              handleAddUser();
            });
          }}
        />
      </ControlGroup>
    );
  };

  renderAddNewPerson = () => {
    const handleAddUser = () => {
      if (this.guardLocked()) return;
      if (!this.state.userSelected || !this.state.userSelected._id) {
        cogoToast.error("Please select a user");
        return;
      }
      if (!this.state.role) {
        cogoToast.error("Please select a role");
        return;
      }
      const newPerson = {
        userName: this.state.userSelected.name,
        userId: this.state.userSelected._id,
        role: this.state.role,
      };

      this.updateData({
        keyPeople: [...this.state.keyPeople, newPerson],
      });
      this.setState({
        userSelected: null,
        role: null,
      });
    };

    return (
      <ControlGroup>
        <Tag intent="primary" large minimal style={{ color: "var(--text-primary)" }}>
          Add Personnel Role
        </Tag>
        <Tag intent="primary" large minimal style={{ color: "var(--text-primary)" }}>
          Role
        </Tag>
        <SingleSelectWrapper
          items={this.roles.filter((each) => {
            let idx = _.findIndex(this.state.keyPeople, (x) => x.role === each);
            return idx === -1;
          })}
          selectedValue={this.state.role}
          onChange={(value) => {
            if (this.guardLocked()) return;
            this.setState({ role: value });
          }}
          showSelectProp={true}
        />
        {this.renderAsyncSearchers(
          "User",
          "Search User",
          "userSearching",
          "userSearchResults",
          "userSelected",
          `/users/${this.props.authStore?.prefix || "default"}/search`,
          handleAddUser
        )}
        <Button icon="add" outlined onClick={handleAddUser} />
      </ControlGroup>
    );
  };

  renderKeyPeople = () => {
    const isLocked = this.props.canEdit === false;
    return this.roles.map((each) => {
      let idx = _.findIndex(this.state.keyPeople, (x) => x.role === each);
      if (idx > -1) {
        return (
          <ControlGroup key={each}>
            <Tag
              large
              intent="success"
              minimal
              icon="endorsed"
              onRemove={isLocked ? undefined : () => {
                let ll = this.state.keyPeople;
                ll.splice(idx, 1);
                this.updateData({ keyPeople: ll });
              }}
            >
              {each}
            </Tag>
            <Tag large intent="primary" icon="person">
              {this.state.keyPeople[idx].userName}
            </Tag>
          </ControlGroup>
        );
      } else {
        return (
          <ControlGroup key={each}>
            <Tag minimal large intent="warning">
              {each}
            </Tag>
            <Tag large minimal style={{ color: "var(--text-secondary)" }}>
              Not Chosen
            </Tag>
          </ControlGroup>
        );
      }
    });
  };

  renderKeyPeopleSection = () => {
    const isLocked = this.props.canEdit === false;
    return (
      <Card elevation={Elevation.FOUR} style={{ backgroundColor: "var(--bg-surface)", marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Key People</H3>
          {this.state.isKeyPeopleDirty && !isLocked && (
            <Button
              intent="primary"
              icon="floppy-disk"
              onClick={() => this.onSave("keyPeople")}
              loading={this.state.isSavingKeyPeople}
              disabled={this.state.isSavingKeyPeople}
              small
            >
              {this.state.isSavingKeyPeople ? "Saving..." : "Save"}
            </Button>
          )}
        </div>
        <div>
          {!isLocked && this.renderAddNewPerson()}
          {!isLocked && <><br /><br /></>}
          {this.renderKeyPeople()}
          <br />
        </div>
      </Card>
    );
  };

  render() {
    const isLocked = this.props.canEdit === false;
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {isLocked && (
          <Card
            elevation={Elevation.TWO}
            style={{
              backgroundColor: "#5c255c",
              marginBottom: "16px",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Tag intent="warning" icon="lock" large minimal>
              Locked
            </Tag>
            <Text style={{ color: "#f5f8fa" }}>
              Key events and people are locked because this surgery has been discharged or cancelled.
            </Text>
          </Card>
        )}
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1", minWidth: "400px" }}>{this.renderKeyEventsSection()}</div>
          <div style={{ flex: "1", minWidth: "400px" }}>{this.renderKeyPeopleSection()}</div>
        </div>
      </div>
    );
  }
}

export default inject("authStore")(observer(KeyEventsTab));
